import { requestJson } from "../api/apiClient";

const BASE = "/api/partner/casamapa";
const AUTH_OPTIONS = { skipAuthRedirect: true };

function buildQs(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value != null && value !== "");
  if (!entries.length) return "";
  return `?${new URLSearchParams(Object.fromEntries(entries)).toString()}`;
}

export function fetchCasaMapaPartnerInventorySummary() {
  return requestJson(`${BASE}/inventory-summary`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerAgencies(params = {}) {
  return requestJson(`${BASE}/agencies${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerAgency(agencySlug) {
  return requestJson(`${BASE}/agencies/${encodeURIComponent(agencySlug)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerAgencyListings(agencySlug, params = {}) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/listings${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchCasaMapaPartnerAgencyLeads(agencySlug, params = {}) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/leads${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchCasaMapaPartnerListings(params = {}) {
  return requestJson(`${BASE}/listings${buildQs(params)}`, AUTH_OPTIONS);
}

export async function fetchCasaMapaPartnerListingHealth(params = {}) {
  const query = buildQs(params);
  const paths = [`${BASE}/listings/health${query}`, `${BASE}/listing-health${query}`];

  let lastError = null;
  for (const path of paths) {
    try {
      return await requestJson(path, AUTH_OPTIONS);
    } catch (error) {
      lastError = error;
      if (error.status !== 404 || !/route not found/i.test(String(error.message || ""))) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Listing health API is unavailable.");
}

export function fetchCasaMapaPartnerListingSync(params = {}) {
  return requestJson(`${BASE}/listings/sync${buildQs(params)}`, AUTH_OPTIONS);
}

export function updateCasaMapaPartnerListingSync(payload) {
  return requestJson(`${BASE}/listings/sync`, {
    ...AUTH_OPTIONS,
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function updateCasaMapaPartnerListingVisibility(listingId, payload) {
  return requestJson(`${BASE}/listings/${encodeURIComponent(listingId)}/visibility`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchCasaMapaPartnerListingHealthDetail(listingId) {
  const encoded = encodeURIComponent(listingId);
  const paths = [
    `${BASE}/listings/${encoded}/health`,
    `${BASE}/listings/${encoded}`,
    `${BASE}/listing-health/${encoded}`,
  ];

  let lastError = null;
  for (const path of paths) {
    try {
      return await requestJson(path, AUTH_OPTIONS);
    } catch (error) {
      lastError = error;
      if (error.status !== 404 || !/route not found/i.test(String(error.message || ""))) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Listing detail API is unavailable.");
}

export function fetchCasaMapaPartnerListingComps(listingId) {
  const encoded = encodeURIComponent(listingId);
  return requestJson(`${BASE}/listings/${encoded}/comps`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerPerformance(params = {}) {
  return requestJson(`${BASE}/performance${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerAgencyPerformance(agencySlug, params = {}) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/performance${buildQs(params)}`,
    AUTH_OPTIONS
  );
}

export function fetchCasaMapaPartnerVendors(params = {}) {
  return requestJson(`${BASE}/vendors${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerFreeListingUsage(agencySlug) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings/usage`,
    AUTH_OPTIONS
  );
}

export function fetchCasaMapaPartnerFreeListings(agencySlug) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings`,
    AUTH_OPTIONS
  );
}

export function createCasaMapaPartnerFreeListing(agencySlug, payload) {
  return requestJson(`${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCasaMapaPartnerFreeListing(agencySlug, listingId, payload) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings/${encodeURIComponent(listingId)}`,
    {
      ...AUTH_OPTIONS,
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function publishCasaMapaPartnerFreeListing(agencySlug, listingId) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings/${encodeURIComponent(listingId)}/publish`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export function unpublishCasaMapaPartnerFreeListing(agencySlug, listingId) {
  return requestJson(
    `${BASE}/agencies/${encodeURIComponent(agencySlug)}/free-listings/${encodeURIComponent(listingId)}/unpublish`,
    {
      ...AUTH_OPTIONS,
      method: "POST",
      body: JSON.stringify({}),
    }
  );
}

export function fetchCasaMapaPartnerMarketAreas() {
  return requestJson(`${BASE}/market/areas`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerMarketBrief(params = {}) {
  return requestJson(`${BASE}/market/brief${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerMarketHeatmap(params = {}) {
  return requestJson(`${BASE}/market/heatmap${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerMarketTrends(params = {}) {
  return requestJson(`${BASE}/market/trends${buildQs(params)}`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerPulsePrefs() {
  return requestJson(`${BASE}/market/pulse/prefs`, AUTH_OPTIONS);
}

export function updateCasaMapaPartnerPulsePrefs(payload) {
  return requestJson(`${BASE}/market/pulse/prefs`, {
    ...AUTH_OPTIONS,
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function fetchCasaMapaPartnerPulseDigests() {
  return requestJson(`${BASE}/market/pulse`, AUTH_OPTIONS);
}

export function fetchCasaMapaPartnerPulseDigest(digestId) {
  return requestJson(`${BASE}/market/pulse/${encodeURIComponent(digestId)}`, AUTH_OPTIONS);
}

export function generateCasaMapaPartnerPulseDigest(payload = {}) {
  return requestJson(`${BASE}/market/pulse/generate`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchCasaMapaPartnerDataContributions(params = {}) {
  return requestJson(`${BASE}/data-contributions${buildQs(params)}`, AUTH_OPTIONS);
}

export function submitCasaMapaPartnerDataContribution(payload) {
  return requestJson(`${BASE}/data-contributions`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchCasaMapaPartnerCrmLeadExchange() {
  return requestJson(`${BASE}/crm-lead-exchange`, AUTH_OPTIONS);
}

export function updateCasaMapaPartnerCrmLeadExchange(payload) {
  return requestJson(`${BASE}/crm-lead-exchange`, {
    ...AUTH_OPTIONS,
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function rotateCasaMapaPartnerCrmOutboundSecret() {
  return requestJson(`${BASE}/crm-lead-exchange/rotate-outbound-secret`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: "{}",
  });
}

export function rotateCasaMapaPartnerCrmInboundToken() {
  return requestJson(`${BASE}/crm-lead-exchange/rotate-inbound-token`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: "{}",
  });
}

export function testCasaMapaPartnerCrmOutbound() {
  return requestJson(`${BASE}/crm-lead-exchange/test-outbound`, {
    ...AUTH_OPTIONS,
    method: "POST",
    body: "{}",
  });
}
